import { fetchJSON as fetchAPI } from './client';

export type ValidationDashboardSnapshot = {
  tenant_id: string; success_rate_pct: number; accuracy_pct: number;
  learning_velocity: number; total_validated: number; business_impact_usd: string;
  confidence_trend: string; top_performing_count: number; worst_performing_count: number;
  generated_at: string;
};

export type ValidatedRecommendation = {
  id: string; source: string; type: string; title: string; lifecycle_stage: string;
  initial_confidence: number; current_confidence: number; confidence_trend: string;
  success_score: number; business_impact?: string; validated: boolean;
};

export type OutcomeMeasurement = {
  recommendation_id: string; metrics: Record<string, string>;
  measured_impact: string; estimated_impact: string; predicted_impact: string;
  observation_window: string; measured_at: string;
};

export type ConfidenceEvolution = {
  recommendation_id: string; initial_confidence: number; current_confidence: number;
  historical_avg: number; trend: string; evidence_count: number;
  successful_outcomes: number; failed_outcomes: number; recommendation_age: string;
};

export type PatternEntry = { id: string; type: string; name: string; category: string; win_rate_pct: number; sample_size: number };
export type BenchmarkEntry = { id: string; dimension: string; benchmark: string; value: string; comparison: string; percentile: number };
export type FailureClassification = { id: string; recommendation_id: string; classification: string; title: string; recoverable: boolean };
export type LearningUpdate = { id: string; target: string; action: string; impact: string; timestamp: string };
export type EvidenceRecord = { recommendation_id: string; type: string; title: string; value: string; source: string; recorded_at: string };
export type ValidationTimelineEvent = { id: string; type: string; title: string; actor: string; timestamp: string };

const COMMAND_BASE = import.meta.env.VITE_COMMANDCENTER_API ?? '/api/v1/command';
const fetchC = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${COMMAND_BASE}${path}`, init);

export const recommendationValidationApi = {
  dashboard: () => fetchC<ValidationDashboardSnapshot>('/recommendation-validation/dashboard'),
  recommendations: () => fetchC<{ recommendations: ValidatedRecommendation[] }>('/recommendation-validation/recommendations').then((r) => r.recommendations),
  outcomes: (id: string) => fetchC<OutcomeMeasurement>(`/recommendation-validation/outcomes/${id}`),
  attribution: (id: string) => fetchC<{ recommendation_id: string; touchpoints: { source: string; weight: number; impact_type: string }[] }>(`/recommendation-validation/attribution/${id}`),
  confidence: (id: string) => fetchC<ConfidenceEvolution>(`/recommendation-validation/confidence/${id}`),
  patterns: () => fetchC<{ patterns: PatternEntry[] }>('/recommendation-validation/patterns').then((r) => r.patterns),
  benchmarks: () => fetchC<{ benchmarks: BenchmarkEntry[] }>('/recommendation-validation/benchmarks').then((r) => r.benchmarks),
  failures: () => fetchC<{ failures: FailureClassification[] }>('/recommendation-validation/failures').then((r) => r.failures),
  learning: () => fetchC<{ updates: LearningUpdate[] }>('/recommendation-validation/learning').then((r) => r.updates),
  evidence: (id: string) => fetchC<{ evidence: EvidenceRecord[] }>(`/recommendation-validation/evidence/${id}`).then((r) => r.evidence),
  history: () => fetchC<{ events: ValidationTimelineEvent[] }>('/recommendation-validation/history').then((r) => r.events),
};
